import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import GenderApiService from '../../../api/GenderApiService';
import AuthorizedContent from '../../../components/AuthorizedContent';
import GenderDetailsForm from '../../../components/forms/GenderDetailsForm';
import EditCancelButton from '../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../config/Breadcrumbs';
import Routes from '../../../config/Routes';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as DeleteGenderHandler from '../../../handlerModels/DeleteGenderHandler';
import * as GetGenderDetailsHandler from '../../../handlerModels/GetGenderDetailsHandler';
import GenderDTO from '../../../models/GenderDTO';
import HeaderPortal from '../../../portals/HeaderPortal';
import BaseFormState from '../../../redux/bases/BaseFormState';
import HistoryUtil from '../../../utils/HistoryUtil';
import PageStayPrompt from '../../../utils/PageStayPrompt';
import ParameterUtil from '../../../utils/ParameterUtil';

interface GenderDetailsPageState extends BaseFormState {
  genderId: number;
  gender?: GenderDTO;
}

interface GenderDetailsPageProps {
}

class GenderDetailsPage extends React.Component<GenderDetailsPageProps & RouteComponentProps<RouteObject>, GenderDetailsPageState> {
  private readonly _genderDetailsRef = React.createRef<GenderDetailsForm>();

  constructor(props: GenderDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      genderId: 0
    };
  }

  componentDidMount() {
    const genderId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (genderId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false })
    }
    else {
      const genderIdNumber = Number.parseInt(genderId);
      this.loadGender(genderIdNumber);
    }
  }

  private loadGender = (genderId: number) => {
    this.setState({ genderId: genderId });

    GenderApiService.getGenderDetails(genderId)
      .then((results: GetGenderDetailsHandler.Result) => {
        if (results.gender) {
          this.setState({ gender: results.gender });
        }
      }).catch(() => {
        this.setState({ error: true });
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private deleteConfirmation = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this Gender?',
      okText: 'Delete',
      onOk: () => this.deleteGender(id)
    });
  }

  private deleteGender = (genderId: number) => {
    this.setState({ submitting: true });

    const request = DeleteGenderHandler.Request.create({
      genderId: genderId,
    });

    GenderApiService.deleteGender(request)
      .then((result: DeleteGenderHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('Gender was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Gender could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToGender();
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._genderDetailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private genderDetailsSaved = (genderId: number) => {
    this.setState({ isEditing: false });
    if (genderId != this.state.genderId) {
      HistoryUtil.replace(Routes.generate(Routes.GENDER_DETAILS, { id: genderId }));
    }
    else {
      this.loadGender(genderId);
    }
  }

  private goToGender = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.genderId == 0 ? 'New Gender' : this.state.gender?.name ?? '...';

    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToGender}
          breadcrumbs={Breadcrumbs.genderDetails(title, this.state.genderId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content>
    );
  }

  renderDetailsCard() {
    const editButton = this.state.genderId == 0 ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_GENDER]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton}>
        <GenderDetailsForm
          ref={this._genderDetailsRef}
          genderId={this.state.genderId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.genderDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete Gender' key={this.state.genderId} onClick={() => this.deleteConfirmation(this.state.genderId)}  >
          Delete Gender
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_GENDER]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(GenderDetailsPage);
